/**
 * RankService — the player's rank, exp-within-rank and earned achievements,
 * over StorageLike (so writes flow through the synced-storage decorator and
 * `?debug` sandboxing, like every other progress key).
 *
 * Keys: hambi.rank (int 1–40), hambi.exp (int ≥ 0, within the current rank),
 * hambi.achievements (JSON AchievementId[]). All three sync to game_saves.
 * Spec: docs/superpowers/specs/2026-08-25-achievements-ranks-design.md
 */

import type { StorageLike } from '../account/saveSync';
import {
  ACHIEVEMENT_EXP,
  achievementById,
  isAchievementId,
  type Achievement,
  type AchievementId,
} from '../config/achievements';
import { addExp, clampRank } from './rankMath';

export const RANK_KEY = 'hambi.rank';
export const EXP_KEY = 'hambi.exp';
export const ACHIEVEMENTS_KEY = 'hambi.achievements';

export class RankService {
  constructor(private readonly storage: StorageLike) {}

  /** Current rank (1–40; corrupt/absent values read as 1). */
  rank(): number {
    const n = parseInt(this.storage.getItem(RANK_KEY) ?? '', 10);
    return Number.isFinite(n) ? clampRank(n) : 1;
  }

  /** Exp accumulated within the current rank (≥ 0). */
  exp(): number {
    const n = parseInt(this.storage.getItem(EXP_KEY) ?? '', 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  /** Earned achievement ids (foreign entries dropped). */
  earned(): AchievementId[] {
    const raw = this.storage.getItem(ACHIEVEMENTS_KEY);
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw) as unknown;
      return Array.isArray(arr) ? arr.filter(isAchievementId) : [];
    } catch {
      return [];
    }
  }

  has(id: AchievementId): boolean {
    return this.earned().includes(id);
  }

  /**
   * Award an achievement: records it and pays its tier exp. Returns the
   * Achievement when NEWLY earned, null when already held (no exp, no
   * notification — achievements can never be earned twice).
   */
  award(id: AchievementId): Achievement | null {
    if (this.has(id)) return null;
    const a = achievementById(id);
    this.storage.setItem(ACHIEVEMENTS_KEY, JSON.stringify([...this.earned(), id]));
    this.addExp(ACHIEVEMENT_EXP[a.tier]);
    return a;
  }

  /** Add exp with rank carryover (shared rankMath rules). */
  addExp(gained: number): void {
    const next = addExp(this.rank(), this.exp(), gained);
    this.storage.setItem(RANK_KEY, String(next.rank));
    this.storage.setItem(EXP_KEY, String(next.exp));
  }

  /** Set the rank outright (dev command), clamping to 1–40; exp resets to 0. */
  setRank(n: number): void {
    this.storage.setItem(RANK_KEY, String(clampRank(n)));
    this.storage.setItem(EXP_KEY, '0');
  }
}
